import React, { useState } from 'react';
import { Upload, CheckCircle, Info, Shirt, Download, Sparkles, X, AlertTriangle } from 'lucide-react';

export default function DTFDPIChecker() {
  const [imageInfo, setImageInfo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showInches, setShowInches] = useState(true);
  const [originalImage, setOriginalImage] = useState(null);
  const [enhancedPreview, setEnhancedPreview] = useState(null);
  const [selectedEnhancement, setSelectedEnhancement] = useState(null);

  const calculateDPI = (widthPx, heightPx) => {
    const dtfSizes = [
      { name: 'Pocket Print', width: 4, type: 'Small Detail' },
      { name: 'Left Chest', width: 4, type: 'Logo' },
      { name: 'Youth Front', width: 10, type: 'Youth T-Shirt' },
      { name: 'Adult Front', width: 12, type: 'Adult T-Shirt' },
      { name: 'Adult Back', width: 12, type: 'Back Design' },
      { name: 'Oversized Print', width: 14, type: 'Large Design' },
      { name: 'Gangsheet (5ft or Less)', width: 30, type: '30" Wide, Up to 60" Tall' },
      { name: 'Gangsheet (6ft or Longer)', width: 30, type: '30" Wide, 61"+ Tall' },
    ];

    return dtfSizes.map(size => {
      const dpi = Math.round(widthPx / size.width);
      const aspectRatio = heightPx / widthPx;
      const height = size.width * aspectRatio;
      const isLargeGangsheet = height > 60;
      
      // Calculate recommended max print size based on original resolution
      const recommendedMaxWidth = Math.floor((widthPx / 150) * 10) / 10; // 150 DPI minimum for acceptable quality
      const recommendedMaxHeight = Math.floor((heightPx / 150) * 10) / 10;
      
      let quality = 'Not Suitable';
      let color = '#ef4444';
      let recommendation = 'Image resolution too low for quality DTF printing';
      
      if (dpi >= 300) {
        quality = 'Premium DTF';
        color = '#10b981';
        recommendation = 'Perfect for professional DTF transfers with sharp details';
      } else if (dpi >= 200) {
        quality = 'High Quality';
        color = '#1B888C';
        recommendation = 'Great for DTF printing with excellent results';
      } else if (dpi >= 150) {
        quality = 'Acceptable';
        color = '#f59e0b';
        recommendation = 'Suitable for DTF but may show slight quality loss';
      } else if (dpi >= 100) {
        quality = 'Low Quality';
        color = '#f97316';
        recommendation = 'Not recommended - consider upscaling or vector graphics';
      }

      return {
        name: size.name,
        type: size.type,
        width: size.width,
        height: height,
        dpi: dpi,
        quality: quality,
        color: color,
        recommendation: recommendation,
        isLargeGangsheet: isLargeGangsheet,
        recommendedMaxWidth: recommendedMaxWidth,
        recommendedMaxHeight: recommendedMaxHeight
      };
    });
  };

  const handleImageUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please upload a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const printAnalysis = calculateDPI(img.width, img.height);
        
        setOriginalImage(img);
        setImageInfo({
          name: file.name,
          width: img.width,
          height: img.height,
          fileSize: (file.size / 1024 / 1024).toFixed(2),
          megapixels: ((img.width * img.height) / 1000000).toFixed(2),
          printAnalysis
        });
        setPreview(e.target.result);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleImageUpload(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const improveImageQuality = (printSize, targetDPI = 300) => {
    if (!originalImage) return;

    const targetWidth = printSize.width * targetDPI;
    const aspectRatio = originalImage.height / originalImage.width;
    const targetHeight = Math.round(targetWidth * aspectRatio);
    const printHeight = targetHeight / targetDPI;
    const totalPixels = targetWidth * targetHeight;
    const isLargeGangsheet = printHeight > 60;
    
    // Calculate if original image is small
    const isSmallOriginal = originalImage.width < 1000 || originalImage.height < 1000;
    const scaleFactor = targetWidth / originalImage.width;

    if (isLargeGangsheet) {
      const confirmed = window.confirm(
        `⚠️ LARGE GANGSHEET WARNING\n\n` +
        `You're optimizing a ${printHeight.toFixed(0)}" tall gangsheet (${printSize.width}" × ${printHeight.toFixed(0)}") to ${targetWidth.toLocaleString()} × ${targetHeight.toLocaleString()} pixels.\n\n` +
        `File Details:\n` +
        `• Megapixels: ${(totalPixels / 1000000).toFixed(1)} MP\n` +
        `• Processing Time: 30-90 seconds\n` +
        `• File Size: 200-600 MB\n\n` +
        `Recommendations:\n` +
        `✓ Use desktop computer (not mobile)\n` +
        `✓ Close other browser tabs\n` +
        `✓ Stable internet connection\n` +
        `✓ Be patient - don't close window\n\n` +
        `Continue with optimization?`
      );
      
      if (!confirmed) return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(originalImage, 0, 0, targetWidth, targetHeight);

    const previewUrl = canvas.toDataURL('image/png');
    const printWidth = printSize.width;
    
    setEnhancedPreview(previewUrl);
    setSelectedEnhancement({
      printSize: printSize,
      targetDPI: targetDPI,
      width: targetWidth,
      height: targetHeight,
      canvas: canvas,
      scaleFactor: scaleFactor,
      printWidth: printWidth,
      printHeight: printHeight,
      isLargeFile: isLargeGangsheet,
      megapixels: (totalPixels / 1000000).toFixed(1),
      isSmallOriginal: isSmallOriginal,
      originalWidth: originalImage.width,
      originalHeight: originalImage.height
    });
  };

  const downloadEnhancedImage = () => {
    if (!selectedEnhancement) return;

    selectedEnhancement.canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = imageInfo.name.replace(/\.[^/.]+$/, '');
      link.download = `${fileName}_${selectedEnhancement.printWidth}x${selectedEnhancement.printHeight.toFixed(1)}in_${selectedEnhancement.targetDPI}DPI.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png', 1.0);
  };

  const closeEnhancedPreview = () => {
    setEnhancedPreview(null);
    setSelectedEnhancement(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-full bg-teal-700 flex items-center justify-center">
              <CheckCircle className="w-9 h-9 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-5xl font-bold">
              <span className="text-gray-900">SPOT</span>
              <span className="text-teal-700">ON</span>
            </h1>
          </div>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-0.5 w-24 bg-gray-900"></div>
            <p className="text-sm tracking-widest text-gray-600 font-semibold">DPI OPTIMIZER</p>
            <div className="h-0.5 w-24 bg-gray-900"></div>
          </div>
          <p className="text-gray-600 text-base">Upload your image to check print quality & DPI</p>
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-lg p-16 text-center transition-all bg-white ${
            isDragging ? 'border-teal-700 bg-teal-50' : 'border-gray-300 hover:border-teal-700'
          }`}
        >
          <Upload className={`w-20 h-20 mx-auto mb-6 ${isDragging ? 'text-teal-700' : 'text-gray-400'}`} strokeWidth={1.5} />
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">Drop your image here</h3>
          <p className="text-gray-500 mb-6">or click to browse</p>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e.target.files[0])}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="inline-block px-8 py-3 bg-teal-700 text-white font-semibold rounded-lg cursor-pointer hover:bg-teal-800 transition-colors"
          >
            Choose File (FREE)
          </label>
        </div>

        {imageInfo && (
          <div className="mt-8 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-teal-700" />
                Design Details
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  {preview && (
                    <div className="relative">
                      <img src={preview} alt="Preview" className="w-full h-80 object-contain bg-gray-50 rounded-lg border-2 border-gray-200" />
                      <div className="absolute top-2 right-2 bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-bold">PREVIEW</div>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Image Specs</h3>
                    <button
                      onClick={() => setShowInches(!showInches)}
                      className="px-4 py-2 bg-teal-700 text-white text-sm font-semibold rounded-lg hover:bg-teal-800 transition-colors"
                    >
                      {showInches ? 'Show Pixels' : 'Show Inches'}
                    </button>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b-2 border-gray-200">
                    <span className="text-gray-600 font-medium">Filename:</span>
                    <span className="text-gray-900 font-semibold text-right truncate ml-2 max-w-xs">{imageInfo.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-2 border-gray-200">
                    <span className="text-gray-600 font-medium">Dimensions:</span>
                    <span className="text-gray-900 font-semibold">
                      {showInches 
                        ? `${(imageInfo.width / 300).toFixed(2)}" × ${(imageInfo.height / 300).toFixed(2)}" @ 300 DPI`
                        : `${imageInfo.width} × ${imageInfo.height} px`
                      }
                    </span>
                  </div>
                  {showInches && (
                    <>
                      <div className="flex justify-between items-center py-3 border-b-2 border-gray-200">
                        <span className="text-gray-600 font-medium">@ 200 DPI:</span>
                        <span className="text-gray-900 font-semibold">{(imageInfo.width / 200).toFixed(2)}" × {(imageInfo.height / 200).toFixed(2)}"</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b-2 border-gray-200">
                        <span className="text-gray-600 font-medium">@ 150 DPI:</span>
                        <span className="text-gray-900 font-semibold">{(imageInfo.width / 150).toFixed(2)}" × {(imageInfo.height / 150).toFixed(2)}"</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between items-center py-3 border-b-2 border-gray-200">
                    <span className="text-gray-600 font-medium">Megapixels:</span>
                    <span className="text-gray-900 font-semibold">{imageInfo.megapixels} MP</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-2 border-gray-200">
                    <span className="text-gray-600 font-medium">File Size:</span>
                    <span className="text-gray-900 font-semibold">{imageInfo.fileSize} MB</span>
                  </div>
                  
                  {/* Quality Warning for Small Images */}
                  {imageInfo.width < 1000 && (
                    <div className="mt-6 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-900">
                          <strong>⚠️ Small Original Image:</strong> Your image is {imageInfo.width}×{imageInfo.height}px. 
                          <br /><br />
                          <strong>Best Print Size:</strong> Up to {(imageInfo.width / 150).toFixed(1)}" × {(imageInfo.height / 150).toFixed(1)}" for acceptable quality
                          <br />
                          <strong>For Larger Prints:</strong> Consider using higher-resolution artwork or vector graphics
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-start gap-2">
                      <Info className="w-5 h-5 text-teal-700 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-teal-900">
                        <strong>DTF Pro Tip:</strong> For best results, use high-resolution images (300 DPI) and ensure your design has transparent backgrounds for complex shapes.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-2 mb-6">
                <Shirt className="w-6 h-6 text-teal-700" />
                <h2 className="text-2xl font-bold text-gray-900">DTF Print Quality Analysis</h2>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-5 mb-6 rounded">
                <p className="text-sm text-blue-900 leading-relaxed">
                  <strong>Direct to Film (DTF) Standards:</strong> For professional DTF transfers, aim for 300 DPI for crisp details. 
                  200 DPI is acceptable for most designs. Below 150 DPI may result in visible pixelation and reduced quality.
                  <br /><br />
                  <strong className="flex items-center gap-1"><Sparkles className="w-4 h-4" /> Print Size Optimization:</strong> This tool resizes images to meet DTF print requirements. It cannot add detail that wasn't in the original image. For sharpest results, start with high-resolution artwork or vector graphics.
                  <br /><br />
                  <strong>📏 Gangsheet Sizes:</strong> 30" wide • Up to 60" tall (fast) • 61"+ tall (desktop recommended, 30-90 sec processing)
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {imageInfo.printAnalysis.map((analysis, index) => (
                  <div key={index} className="border rounded-lg p-5 hover:shadow-md transition-all" style={{ borderColor: analysis.color + '40' }}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{analysis.name}</h3>
                        <p className="text-sm text-gray-600">{analysis.type}</p>
                      </div>
                      <div className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: analysis.color }}>
                        {analysis.quality}
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Print Width:</span>
                        <span className="text-gray-900 font-semibold">{analysis.width}"</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Print Height:</span>
                        <span className="text-gray-900 font-semibold">{analysis.height.toFixed(1)}" (auto)</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Current DPI:</span>
                        <span className="text-2xl font-bold text-gray-900">{analysis.dpi}</span>
                      </div>
                      {analysis.dpi < 150 && (
                        <div className="text-xs text-amber-600 font-semibold mt-2">
                          📏 Best up to: {analysis.recommendedMaxWidth}" × {analysis.recommendedMaxHeight}"
                        </div>
                      )}
                    </div>
                    
                    <div className="bg-gray-200 rounded-full h-3 overflow-hidden mb-3">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((analysis.dpi / 300) * 100, 100)}%`, backgroundColor: analysis.color }}></div>
                    </div>
                    
                    <p className="text-xs text-gray-600 italic">{analysis.recommendation}</p>
                    
                    {analysis.isLargeGangsheet && analysis.dpi < 300 && (
                      <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-500 rounded">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-amber-900">
                            <strong>Large Gangsheet Notice:</strong> This {analysis.height.toFixed(0)}" tall gangsheet will take 30-90 seconds to process. Desktop use recommended for best results.
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {analysis.dpi < 300 && (
                      <button
                        onClick={() => improveImageQuality(analysis, 300)}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-700 text-white text-sm font-semibold rounded-lg hover:bg-teal-800 transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Optimize to 300 DPI Print Size
                        {analysis.isLargeGangsheet && <span className="text-xs ml-1">(~{analysis.height > 70 ? '90' : '60'}s)</span>}
                      </button>
                    )}
                    
                    {analysis.dpi >= 150 && analysis.dpi < 200 && (
                      <button
                        onClick={() => improveImageQuality(analysis, 200)}
                        className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Optimize to 200 DPI Print Size
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-teal-700 rounded-lg shadow-md p-8 text-white">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Shirt className="w-7 h-7" />
                DTF Print Quality Standards
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="font-bold text-2xl mb-2">300+ DPI</div>
                  <div className="text-teal-100 font-medium">Premium DTF Quality</div>
                  <div className="text-teal-200 text-sm mt-1">Perfect for detailed designs</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="font-bold text-2xl mb-2">200-299 DPI</div>
                  <div className="text-teal-100 font-medium">High Quality DTF</div>
                  <div className="text-teal-200 text-sm mt-1">Great for most t-shirt prints</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="font-bold text-2xl mb-2">150-199 DPI</div>
                  <div className="text-teal-100 font-medium">Acceptable Quality</div>
                  <div className="text-teal-200 text-sm mt-1">OK for larger prints</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="font-bold text-2xl mb-2">&lt;150 DPI</div>
                  <div className="text-teal-100 font-medium">Not Recommended</div>
                  <div className="text-teal-200 text-sm mt-1">May show pixelation</div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-white border-opacity-30">
                <h4 className="font-bold text-lg mb-3">DTF Best Practices:</h4>
                <ul className="space-y-2 text-teal-50">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>Use PNG files with transparent backgrounds for best results</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>Minimum 300 DPI for small details and text</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>RGB color mode for digital designs, convert to CMYK for accuracy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>Vector graphics (SVG) are ideal for logos and text - sharpest at any size</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="text-center mt-12 pb-6">
        <div className="flex items-center justify-center gap-1.5 mb-3">
          {[...Array(15)].map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full ${i % 3 === 0 ? 'bg-teal-700' : i % 3 === 1 ? 'bg-teal-500' : 'bg-gray-400'}`} />
          ))}
        </div>
        <p className="text-gray-500 text-xs tracking-widest font-medium">POWERED BY ON THE SPOT DESIGNS</p>
      </div>

      {enhancedPreview && selectedEnhancement && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-teal-700" />
                  Print Optimization Complete
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedEnhancement.printSize.name} - {selectedEnhancement.width} × {selectedEnhancement.height} px @ {selectedEnhancement.targetDPI} DPI ({selectedEnhancement.printWidth}" × {selectedEnhancement.printHeight.toFixed(1)}")
                </p>
              </div>
              <button onClick={closeEnhancedPreview} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Original Image</h4>
                  <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-4">
                    <img src={preview} alt="Original" className="w-full h-64 object-contain" />
                    <div className="mt-3 text-sm text-gray-600 text-center">{imageInfo.width} × {imageInfo.height} px</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    Optimized for Print
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">{selectedEnhancement.targetDPI} DPI</span>
                  </h4>
                  <div className="bg-gray-50 rounded-lg border-2 border-teal-700 p-4">
                    <img src={enhancedPreview} alt="Optimized" className="w-full h-64 object-contain" />
                    <div className="mt-3 text-sm text-teal-700 font-semibold text-center">{selectedEnhancement.width} × {selectedEnhancement.height} px</div>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-700 p-4 rounded mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-teal-700 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-teal-900">
                    <strong>✅ Print Size Optimization Complete!</strong>
                    {selectedEnhancement.isLargeFile && (
                      <>
                        <br />
                        <span className="text-amber-700 font-bold">⚠️ Large Gangsheet ({selectedEnhancement.printHeight.toFixed(0)}" tall) - Download may take 1-2 minutes</span>
                      </>
                    )}
                    <br /><br />
                    <strong>Print Dimensions:</strong> {selectedEnhancement.printWidth}" W × {selectedEnhancement.printHeight.toFixed(1)}" H at {selectedEnhancement.targetDPI} DPI
                    <br />
                    <strong>Pixel Dimensions:</strong> {selectedEnhancement.width.toLocaleString()} × {selectedEnhancement.height.toLocaleString()} px
                    <br />
                    <strong>Total Megapixels:</strong> {selectedEnhancement.megapixels} MP
                    <br />
                    <strong>Scale Factor:</strong> {selectedEnhancement.scaleFactor.toFixed(2)}x larger than original
                    <br />
                    <strong>Estimated File Size:</strong> {selectedEnhancement.isLargeFile ? '200-600 MB' : selectedEnhancement.printHeight > 36 ? '100-300 MB' : '50-150 MB'}
                    <br /><br />
                    <em>File resized from {selectedEnhancement.originalWidth}×{selectedEnhancement.originalHeight}px to {selectedEnhancement.printWidth}" wide × {selectedEnhancement.printHeight.toFixed(1)}" tall (aspect ratio maintained)</em>
                  </div>
                </div>
              </div>

              {/* Quality Warning for Small Original Images */}
              {selectedEnhancement.isSmallOriginal && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                      <strong>⚠️ Print Quality Notice:</strong>
                      <br /><br />
                      Your original image was small ({selectedEnhancement.originalWidth}×{selectedEnhancement.originalHeight}px) and has been upscaled {selectedEnhancement.scaleFactor.toFixed(1)}× to meet print size requirements.
                      <br /><br />
                      <strong>What This Means:</strong>
                      <br />
                      ✅ File Size & DPI: PASS - RIP software will accept this file
                      <br />
                      ⚠️ Visual Sharpness: LIMITED - Upscaling cannot add detail that wasn't in the original
                      <br /><br />
                      <strong>Recommendations:</strong>
                      <br />
                      • Best results at smaller print sizes (6-8 inches or less)
                      <br />
                      • Larger prints may appear soft or slightly blurry
                      <br />
                      • For sharpest results, use higher-resolution artwork or vector graphics
                      <br />
                      • Test print recommended before large production runs
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-6">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <strong>What This Tool Does:</strong> This tool resizes images to meet DTF print size and DPI requirements. It prepares files for RIP software and DTF printing.
                    <br /><br />
                    <strong>What It Cannot Do:</strong> This tool cannot add sharpness or detail that wasn't in the original image. For best results, always start with high-resolution artwork or vector graphics.
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={downloadEnhancedImage}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-teal-700 text-white font-semibold rounded-lg hover:bg-teal-800 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Download Optimized Image
                </button>
                <button onClick={closeEnhancedPreview} className="px-6 py-4 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors">
                  Cancel
                </button>
              </div>

              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>File will be saved as:</strong> {imageInfo.name.replace(/\.[^/.]+$/, '')}_{selectedEnhancement.printWidth}x{selectedEnhancement.printHeight.toFixed(1)}in_{selectedEnhancement.targetDPI}DPI.png
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Optimized for DTF printing: {selectedEnhancement.printWidth}" W × {selectedEnhancement.printHeight.toFixed(1)}" H • {selectedEnhancement.width}×{selectedEnhancement.height} pixels @ {selectedEnhancement.targetDPI} DPI
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
